import React, { useState, useMemo } from 'react';
import { FiPlay, FiCopy, FiTrash2 } from 'react-icons/fi';
import ncbClient from '@/utils/ncbClient';
import dropboxClient from '@/utils/dropboxClient';
import logger from '@/utils/logger';

// ... (Component Implementation)
const ncbEndpoints = {
  getProjects: { method: 'POST', path: '/get-projects', body: {} },
  createProject: { method: 'POST', path: '/create-project', body: { siteTitle: "My Test Project" } },
};
const dropboxEndpoints = {
  listFiles: { method: 'POST', path: '/list', body: { path: '', recursive: false } },
  refreshToken: { method: 'POST', path: '/refresh-token', body: {} },
};

const apiConfig = {
  ncb: {
    title: "NoCodeBackend API Tester",
    client: ncbClient,
    endpoints: ncbEndpoints,
    baseUrl: import.meta.env.VITE_NCB_BASE_URL || 'https://openapi.nocodebackend.com',
    env: {
      "VITE_NCB_INSTANCE_ID": import.meta.env.VITE_NCB_INSTANCE_ID,
    }
  },
  dropbox: {
    title: "Dropbox API Tester",
    client: dropboxClient,
    endpoints: dropboxEndpoints,
    baseUrl: 'https://api.dropboxapi.com/2',
    env: {
      "VITE_DROPBOX_APP_KEY": import.meta.env.VITE_DROPBOX_APP_KEY,
    }
  },
};

function ApiTestForm({ apiType }) {
  const config = apiConfig[apiType];
  const [endpointKey, setEndpointKey] = useState(Object.keys(config.endpoints)[0]);
  const [body, setBody] = useState(JSON.stringify(config.endpoints[endpointKey].body, null, 2));
  const [isLoading, setIsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const selectedEndpoint = config.endpoints[endpointKey];

  const handleRun = async () => {
    setIsLoading(true);
    try {
      let parsedBody = {};
      if (body.trim()) {
        parsedBody = JSON.parse(body);
      }
      switch (`${apiType}:${endpointKey}`) {
        case 'dropbox:listFiles':
          await dropboxClient.listFiles(parsedBody.path, parsedBody.recursive);
          break;
        case 'dropbox:refreshToken':
          await dropboxClient.refreshToken();
          break;
        case 'ncb:getProjects':
           await ncbClient.getProjects();
           break;
        case 'ncb:createProject':
           await ncbClient.createProject(parsedBody);
           break;
        default:
          logger.error("Unknown API endpoint key", { apiType, endpointKey });
      }
    } catch (err) {
      logger.error("API Test Form Error", { error: err.message });
      // Error is already logged by the client
    } finally {
      setIsLoading(false);
    }
  };

  const generateCurl = () => {
    const headers = apiType === 'ncb' 
        ? `-H "apikey: YOUR_NCB_API_KEY" -H "instanceid: ${config.env.VITE_NCB_INSTANCE_ID}"` 
        : `-H "Authorization: Bearer YOUR_DROPBOX_ACCESS_TOKEN"`;
    
    return `curl -X ${selectedEndpoint.method} '${config.baseUrl}${selectedEndpoint.path}' \\
 -H "Content-Type: application/json" \\
 ${headers} \\
 -d '${body}'`;
  };
  
  const handleCopyCurl = () => {
    navigator.clipboard.writeText(generateCurl());
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };
  
  const handleEndpointChange = (e) => {
    const newKey = e.target.value;
    setEndpointKey(newKey);
    setBody(JSON.stringify(config.endpoints[newKey].body, null, 2));
  };


  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="font-bold text-lg text-gray-800">{config.title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <select onChange={handleEndpointChange} value={endpointKey} className="w-full rounded-md border-gray-300">
                {Object.entries(config.endpoints).map(([key, { method, path }]) => (
                    <option key={key} value={key}>{method} {path}</option>
                ))}
            </select>
            <div className="flex items-center space-x-2">
                <button onClick={() => setBody(JSON.stringify(selectedEndpoint.body, null, 2))} className="text-sm text-indigo-600">Use Example</button>
                <button onClick={() => setBody('')} className="text-sm text-red-600">Clear</button>
            </div>
        </div>
        <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">Request Body (JSON)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows="6" className="font-mono text-sm mt-1 block w-full rounded-md border-gray-300"></textarea>
        </div>
        <div className="mt-4 flex space-x-3">
             <button onClick={handleRun} disabled={isLoading} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400">
                <FiPlay className="mr-2"/> {isLoading ? 'Running...' : 'Run Request'}
            </button>
            <button onClick={handleCopyCurl} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <FiCopy className="mr-2"/> {copySuccess ? 'Copied!' : 'Copy cURL'}
            </button>
        </div>
         <div className="mt-6 border-t pt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Environment Status</h4>
            {Object.entries(config.env).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center text-sm">
                    <span className="font-mono text-gray-600">{key}</span>
                    {value ? <span className="text-green-600 font-medium">Set</span> : <span className="text-red-600 font-medium">Missing</span>}
                </div>
            ))}
        </div>
    </div>
  );
}

export default ApiTestForm;